<template>
    <div class="card">
        <DataTable lazy dataKey="uuid"
            stateStorage="local"
            ref="dt"
            :state-key="tableLocalStorageKey"
            :value="paginatedData"
            :paginator="true"
            :rows="rows"
            :rowHover="true"
            sortMode="multiple"
            removableSort
            :loading="isLoading"
            :totalRecords="totalFilteredRecords"
            :currentPage="currentPage"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            :rowsPerPageOptions="rowsPerPageOptions"
            currentPageReportTemplate="Affichage de {first} à {last} sur {totalRecords} entrées"
            @page="onPaginate($event)"
        >
        <template #header>
            <div class="flex flex-row flex-nowrap card-header border-0">
                <div class="flex card-toolbar w-full">
                    <div class="w-full">
                        <span class="p-input-icon-left w-10/12">
                            <i class="pi pi-search" />
                            <InputText class="w-9/12" type="text" v-model="criteria" placeholder="Rechercher une marque" @keyup.enter="onSearchInputDt()" />
                        </span>
                    </div>
                </div>
                <div class="card-toolbar w-full">
                    <div class="flex flex-row items-center justify-between">
                        <Dropdown
                            v-model="selectedSupplier"
                            :options="uniqueSuppliers"
                            optionLabel="name"
                            placeholder="Sélectionner un fournisseur"
                            filter
                            :showClear="true"
                            class="w-64"
                            @change="onSupplierFilterChange"
                        >
                            <template #value="slotProps">
                                <div v-if="slotProps.value">
                                    {{ slotProps.value.name }}
                                </div>
                                <span v-else>Sélectionner un fournisseur</span>
                            </template>
                            <template #option="slotProps">
                                <div>{{ slotProps.option.name }}</div>
                            </template>
                        </Dropdown>
                    </div>
                </div>
            </div>
        </template>
        <div class="card-body pt-0">
            <Column field="brand" header="Marque">
            <template #body="slotProps">
                <span>{{ slotProps.data.brand }}</span>
            </template>
            </Column>
            <Column field="supplier" header="Fournisseur">
            <template #body="slotProps">
                <span>{{ slotProps.data.supplier }}</span>
            </template>
            </Column>
        </div>
        <template #empty>Aucun résultat trouvé.</template>
        </DataTable>
    </div>
</template>

<script lang="ts" setup>
import { SocialEventBrands } from "@/models/product/brands/SocialEventBrands";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import { ref, onMounted, watch, computed } from "vue";
import InputText from "primevue/inputtext";
import swal from "@/_helpers/swal";
import router from "@/router";
import { getPaginatedSupplierForDropDown } from "@/_services/supplier/supplier";
import { fetchBrandsByUrssafCategory } from "@/_services/category/category";
import Dropdown from "primevue/dropdown";

const tableLocalStorageKey = "tableSelectedBrandsToAddInCollection";
const tableData = ref<SocialEventBrands[]>([]);
const rows = ref<number>(10);
const isLoading = ref<boolean>(true);
const totalRecords = ref<number>(0);
const currentPage = ref<number>(0);
const defaultRowsPerPage = 10;
const rowsPerPageOptions = [defaultRowsPerPage, 25, 100, 250];
const page = ref<number>(1);
const criteria = ref<string>("");
const selectedSupplier = ref<{name: string} | null>(null);
const uniqueSuppliers = ref<{name: string}[]>([]);
const originalBrands = ref<SocialEventBrands[]>([]);

const filteredTableData = computed(() => {
    if (!criteria.value && !selectedSupplier.value) {
        return tableData.value;
    }
    
    return tableData.value.filter(item => {
        const matchesBrand = !criteria.value || 
            item.brand.toLowerCase().includes(criteria.value.toLowerCase());
        
        const matchesSupplier = !selectedSupplier.value || 
            item.supplier === selectedSupplier.value.name;
        
        return matchesBrand && matchesSupplier;
    });
});

const totalFilteredRecords = computed(() => {
    return filteredTableData.value.length;
});

const paginatedData = computed(() => {
    const start = currentPage.value * rows.value;
    const end = start + rows.value;
    return filteredTableData.value.slice(start, end);
});

function onSearchInputDt() {
    showSpinner();
    setTimeout(() => {
        isLoading.value = false;
    }, 300);
}

watch(criteria, () => {
    if (criteria.value.length >= 3 || criteria.value.length === 0) {
        onSearchInputDt();
    }
});

onMounted(async () => {
    await populateDataTableWithLocalStorageFilters();
});

function extractUniqueSuppliers(brands: SocialEventBrands[]): {name: string}[] {
    const supplierSet = new Set<string>();
    
    brands.forEach(brand => {
        if (brand.supplier) {
            supplierSet.add(brand.supplier);
        }
    });
    
    return Array.from(supplierSet).map(supplier => ({ name: supplier }));
}

function onSupplierFilterChange() {
    onSearchInputDt();
}

const populateDatatable = async () => {
    showSpinner();

    const socialEventUuid = router.currentRoute.value.params.uuid;

    if (!socialEventUuid) {
        swal.error("Aucun identifiant d'événement URSSAF trouvé dans l'URL.");
        return;
    }

    try {
        isLoading.value = true;

        const {
            brands,
            errorMessage
        } = await fetchBrandsByUrssafCategory(socialEventUuid);

        if (Array.isArray(brands.value)) {
            const formattedBrands = brands.value.map((brandInfo: any) => {
                if (typeof brandInfo === 'object' && brandInfo.brand && brandInfo.supplier) {
                    return {
                        brand: brandInfo.brand,
                        supplier: brandInfo.supplier,
                        isSelected: false
                    };
                } else if (typeof brandInfo === 'string') {
                    const parts = brandInfo.split('|');
                    return {
                        brand: parts[0] || '',
                        supplier: parts[1] || '',
                        isSelected: false
                    };
                }
                return {
                    brand: '',
                    supplier: '',
                    isSelected: false
                };
            });
            
            originalBrands.value = formattedBrands;
            tableData.value = formattedBrands;
            totalRecords.value = formattedBrands.length;
            
            uniqueSuppliers.value = extractUniqueSuppliers(formattedBrands);
        } else {
            tableData.value = [];
            totalRecords.value = 0;
        }

        isLoading.value = false;
    } catch (err: any) {
        swal.error("Une erreur inattendue s'est produite.");
    }
};

async function populateDataTableWithLocalStorageFilters(itemsPerPage?: number, page?: number) {
    let localStorageRowPerPage = getLocalStorageRowsPerPage();
    let localStorageFisrtPageState = getLocalStorageFirstPageState();
    let currentPage = page;
    if (!currentPage) {
        currentPage = localStorageFisrtPageState / localStorageRowPerPage + 1;
    }
    let currentItemsPerPage = itemsPerPage;
    if (!currentItemsPerPage) {
        currentItemsPerPage = localStorageRowPerPage;
    }

    await populateDatatable();
}

const onPaginate = async (e) => {
    showSpinner();
    rows.value = e.rows;
    currentPage.value = e.page;
    page.value = e.page + 1;
    setLocalStorageRowsPerPage(rows.value);
    hideSpinner();
};

const hideSpinner = () => {
    isLoading.value = false;
}

const showSpinner = () => {
    isLoading.value = true;
}

function getLocalStorageData() {
    let storageData = localStorage.getItem(tableLocalStorageKey);
    if (null !== storageData && "undefined" !== storageData) {
        return JSON.parse(storageData);
    }

    return null;
}

function setLocalStorageRowsPerPage(rowPerPage: number) {
    let storageData = localStorage.getItem(tableLocalStorageKey);
    if (storageData) {
        let localStorageObject = JSON.parse(storageData);
        localStorageObject.rows = rowPerPage;
        localStorage.setItem(tableLocalStorageKey, JSON.stringify(localStorageObject));
    }
}

function getLocalStorageRowsPerPage() {
    let localStorageData = getLocalStorageData();
    if (null !== localStorageData && localStorageData.rows) {
        return localStorageData.rows;
    }

    return defaultRowsPerPage;
}

function getLocalStorageFirstPageState() {
    let localStorageData = getLocalStorageData();
    if (null !== localStorageData && localStorageData.first) {
        return localStorageData.first;
    }

    return 0;
}
</script>